// WWI ammo dump — a low sandbag-walled enclosure (open to the front) holding
// stacked olive ammo crates with hazard-yellow stencil stripes and a few
// upright brass shells leaning in the corner.
import * as THREE from "three";
import { defineModel, styleV3, type Part } from "../../lib";

const { box } = styleV3;

const SANDBAG = "#a08d5f";
const SANDBAG_DK = "#8e7c52";
const OLIVE = "#5f6b3c";
const HAZARD = "#d9a13b";
const BRASS = "#b08d3a";
const MUD = "#5d4a33";

// A two-course sandbag wall segment: a fat lower course, a slightly narrower
// offset upper course, plus rounded cap bags to break the silhouette.
function wall(cx: number, cz: number, len: number, alongX: boolean): Part[] {
  const dims = (l: number, t: number, h: number): [number, number, number] =>
    alongX ? [l, h, t] : [t, h, l];
  const capOffset = (d: number): [number, number, number] =>
    alongX ? [d, 0, 0] : [0, 0, d];
  const parts: Part[] = [
    box(...dims(len, 0.15, 0.12), SANDBAG, [cx, 0.06, cz]),
    box(...dims(len - 0.08, 0.14, 0.1), SANDBAG_DK, [cx, 0.17, cz]),
  ];
  // Individual cap bags on top, slightly squashed boxes.
  for (const d of [-len / 3, 0, len / 3]) {
    const [ox, , oz] = capOffset(d);
    parts.push(box(...dims(0.13, 0.13, 0.07), SANDBAG, [cx + ox, 0.25, cz + oz], [0, alongX ? 0.12 : 0, 0]));
  }
  return parts;
}

// An olive ammo crate with a hazard-yellow stencil stripe on its front face.
function crate(x: number, y: number, z: number, yaw: number): Part[] {
  const s = Math.sin(yaw), c = Math.cos(yaw);
  return [
    box(0.24, 0.13, 0.17, OLIVE, [x, y + 0.065, z], [0, yaw, 0]),
    // Stencil stripe sits just proud of the +z face.
    box(0.14, 0.035, 0.01, HAZARD, [x + s * 0.09, y + 0.075, z + c * 0.09], [0, yaw, 0]),
  ];
}

// An upright shell: brass body with an olive nose cone, leaning slightly.
// The lean is about z, so the shell axis is (−sin, cos, 0); the nose cone is
// placed by walking up that axis from the body centre.
function uprightShell(x: number, z: number, lean: number): Part[] {
  const h = 0.24, r = 0.042, nose = 0.1;
  const ax = -Math.sin(lean), ay = Math.cos(lean);
  const cx = x, cy = h / 2; // body centre; base stays near the ground
  return [
    {
      geometry: new THREE.CylinderGeometry(r, r, h, 8),
      color: BRASS,
      position: [cx, cy, z],
      rotation: [0, 0, lean],
    },
    {
      geometry: new THREE.ConeGeometry(r * 0.9, nose, 8),
      color: OLIVE,
      position: [cx + ax * (h / 2 + nose / 2), cy + ay * (h / 2 + nose / 2), z],
      rotation: [0, 0, lean],
    },
  ];
}

export const ammoDump = defineModel({
  id: "ammo-dump",
  tiles: { w: 1, h: 1 },
  batchId: "wwi",
  name: "Ammo Dump",
  tags: ["prop", "wwi", "military", "ammunition", "storage", "sandbags"],
  summary: "A sandbag-walled enclosure of stacked ammo crates and upright shells.",
  author: "Fable 5",
  footprint: 0.45,
  parts(): Part[] {
    return [
      // Mud floor pad.
      box(0.82, 0.025, 0.82, MUD, [0, 0.0125, 0]),
      // Sandbag walls: back and both sides, open to the front (+z).
      ...wall(0, -0.37, 0.82, true),
      ...wall(-0.37, -0.02, 0.58, false),
      ...wall(0.37, -0.02, 0.58, false),
      // Crate stack: two on the ground, one on top, one loose and skewed.
      ...crate(-0.12, 0.025, -0.16, 0),
      ...crate(0.14, 0.025, -0.18, 0.18),
      ...crate(0.0, 0.155, -0.17, -0.1),
      ...crate(0.12, 0.025, 0.12, -0.5),
      // A few upright shells leaning in the open corner.
      ...uprightShell(-0.22, 0.14, 0.1),
      ...uprightShell(-0.13, 0.2, -0.06),
      ...uprightShell(-0.26, 0.26, 0.16),
    ];
  },
  animations: [],
});
